import axios from "axios";
import { triggerSessionAlert } from "../utils/SessionAlertHelper";

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

// Helper function to validate formData
const validateFormData = (formData) => {
  if (!formData || typeof formData !== "object") {
    return {
      success: false,
      message: "Invalid form data provided",
    };
  }
  if (!formData.filters || typeof formData.filters !== "object") {
    return {
      success: false,
      message: "Filters object is required in form data",
    };
  }
  return { success: true };
};

// Helper function to process referral data
const processReferralData = (referralValue) => {
  console.log("Processing referral data, referralValue:", referralValue); // Debug log
  return {
    referral: referralValue || "", // Ensure referral is a string (empty or valid ID)
    ...(referralValue !== "NA" && referralValue !== "" && referralValue !== undefined && { hasReferral: "true" }), // Set hasReferral to "true" for valid referral
    ...(referralValue === "NA" || referralValue === "" || referralValue === undefined && { hasReferral: "false" }), // Set hasReferral to "false" for no referral
    // Note: Backend expects hasReferral as "true" or "false" strings
    // - { referral: "", hasReferral: "false" } filters for customer.referral: null
    // - { referral: "<id>", hasReferral: "true" } filters for specific referral ID
    // - { referral: "" } (no hasReferral) returns all orders
  };
};

// Function to fetch orders data from the API
export const fetchOrderData = async (formData) => {
  try {
    const validation = validateFormData(formData);
    if (!validation.success) {
      return validation;
    }

    // Process referral data
    const referralData = processReferralData(formData.filters?.referral);
    console.log("Referral data processed:", referralData); // Debug log

    const requestPayload = {
      ...formData,
      filters: {
        ...formData.filters,
        ...referralData,
      },
    };
    console.log("Sending request payload:", JSON.stringify(requestPayload, null, 2)); // Debug log

    const response = await axios.post(
      `${API_BASE_URL}/orders`,
      requestPayload,
      {
        withCredentials: true,
      }
    );

    console.log("API response:", response.data); // Debug log

    return {
      success: true,
      data: response.data.data.orders,
      totalPages: response.data.data.totalPages,
      currentPage: response.data.data.currentPage,
      totalCount: response.data.data.totalOrders,
      totalRevenue:
        response.data.data.totalSales?.$numberDecimal ||
        response.data.data.totalSales ||
        "0",
    };
  } catch (error) {
    console.error("Error fetching orders:", error); // Debug log
    if (!error.response) {
      window.location.href = "/server-down";
      return {
        success: false,
        message:
          "Cannot connect to the server. Please check your network or try again later.",
        error: {
          code: "NETWORK_ERROR",
          message: error.message || "Network Error",
          solution: "Ensure the backend server is running and reachable.",
        },
      };
    }
    if (error.response?.status === 401) {
      triggerSessionAlert("Your session has expired. Please sign in again.");
      return;
    }
    if (error.response?.status >= 500) {
      window.location.href = "/server-down";
      return;
    }
    return {
      success: false,
      message:
        error.response?.data?.message ||
        "An error occurred while fetching orders.",
      error: error.response?.data?.error || {
        code: error.response?.status || 500,
        message: error.message || "Internal Server Error",
        solution: "Please try again later",
      },
    };
  }
};

// Function to fetch export orders data from the API
export const GetExportOrderData = async (formData) => {
  try {
    const referralData = processReferralData(formData.filters?.referral);
    console.log("Export referral data processed:", referralData); // Debug log

    const requestPayload = {
      ...formData,
      filters: {
        ...formData.filters,
        ...referralData,
      },
    };
    console.log("Export request payload:", JSON.stringify(requestPayload, null, 2)); // Debug log

    const response = await axios.post(
      `${API_BASE_URL}/orders/export`,
      requestPayload,
      {
        withCredentials: true,
      }
    );

    console.log("Export API response:", response.data); // Debug log

    return {
      success: response.data.success,
      data: response.data.data.orders,
      totalCount: response.data.data.totalOrders,
    };
  } catch (error) {
    console.error("Error fetching export orders:", error); // Debug log
    if (!error.response) {
      window.location.href = "/server-down";
      return {
        success: false,
        message:
          "Cannot connect to the server. Please check your network or try again later.",
        error: {
          code: "NETWORK_ERROR",
          message: error.message || "Network Error",
          solution: "Ensure the backend server is running and reachable.",
        },
      };
    }
    if (error.response?.status === 401) {
      triggerSessionAlert("Your session has expired. Please sign in again.");
      return;
    }
    if (error.response?.status >= 500) {
      window.location.href = "/server-down";
      return;
    }
    return {
      success: false,
      message:
        error.response?.data?.message ||
        "An error occurred while fetching orders.",
      error: error.response?.data?.error || {
        code: error.response?.status || 500,
        message: error.message || "Internal Server Error",
        solution: "Please try again later",
      },
    };
  }
};

// Other functions (updateOrderStatus, updatePaymentStatus, handleStatusUpdate, getReferralCodes) remain unchanged
export const updateOrderStatus = async (orderId, newStatus, note = "") => {
  try {
    if (!orderId) {
      return {
        success: false,
        message: "Order ID is required to update payment status",
      };
    }
    if (!newStatus) {
      return {
        success: false,
        message: "New status is required to update payment status",
      };
    }
    if (!note) {
      return {
        success: false,
        message: "Note is required to update payment status",
      };
    }
    const response = await axios.patch(
      `${API_BASE_URL}/orders/order-status`,
      {
        orderId: orderId,
        orderStatus: newStatus,
        orderNote: note,
      },
      { withCredentials: true }
    );

    return {
      success: response.data.success,
      message: response.data.message,
      data: response.data.data,
    };
  } catch (error) {
    if (!error.response) {
      window.location.href = "/server-down";
      return {
        success: false,
        message:
          "Cannot connect to the server. Please check your network or try again later.",
        error: {
          code: "NETWORK_ERROR",
          message: error.message || "Network Error",
          solution: "Ensure the backend server is running and reachable.",
        },
      };
    }
    if (error.response?.status === 401) {
      triggerSessionAlert("Your session has expired. Please sign in again.");
      return;
    }
    if (error.response?.status >= 500) {
      window.location.href = "/server-down";
      return;
    }
    return {
      success: false,
      message:
        error.response?.data?.message ||
        "An error occurred while updating order status.",
      error: error.response?.data?.error || {
        code: error.response?.status || 500,
        message: error.message || "Internal Server Error",
        solution: "Please try again later",
      },
    };
  }
};

export const updatePaymentStatus = async (orderId, newStatus, note = "") => {
  try {
    if (!orderId) {
      return {
        success: false,
        message: "Order ID is required to update payment status",
      };
    }
    if (!newStatus) {
      return {
        success: false,
        message: "New status is required to update payment status",
      };
    }
    if (!note) {
      return {
        success: false,
        message: "Note is required to update payment status",
      };
    }
    const response = await axios.patch(
      `${API_BASE_URL}/orders/payment-status`,
      {
        orderId: orderId,
        paymentStatus: newStatus,
        paymentNote: note,
      },
      { withCredentials: true }
    );

    return {
      success: response.data.success,
      message: response.data.message,
      data: response.data.data,
    };
  } catch (error) {
    if (!error.response) {
      window.location.href = "/server-down";
      return {
        success: false,
        message:
          "Cannot connect to the server. Please check your network or try again later.",
        error: {
          code: "NETWORK_ERROR",
          message: error.message || "Network Error",
          solution: "Ensure the backend server is running and reachable.",
        },
      };
    }
    if (error.response?.status === 401) {
      triggerSessionAlert("Your session has expired. Please sign in again.");
      return;
    }
    if (error.response?.status >= 500) {
      window.location.href = "/server-down";
      return;
    }
    return {
      success: false,
      message:
        error.response?.data?.message ||
        "An error occurred while updating order payment status.",
      error: error.response?.data?.error || {
        code: error.response?.status || 500,
        message: error.message || "Internal Server Error",
        solution: "Please try again later",
      },
    };
  }
};

export const handleStatusUpdate = async (updateData) => {
  const { id, type, newStatus, note } = updateData;

  try {
    if (type === "order") {
      return await updateOrderStatus(id, newStatus, note);
    } else if (type === "payment") {
      return await updatePaymentStatus(id, newStatus, note);
    } else {
      throw new Error("Invalid update type specified");
    }
  } catch (error) {
    throw error;
  }
};

export const getReferralCodes = async () => {
  try {
    const response = await axios.get(`${API_BASE_URL}/referral/only`, {
      withCredentials: true,
    });

    return {
      success: response.data.success,
      message: response.data.message,
      referralCodes: response.data.data.referrals.map((ref) => ({
        id: ref.id,
        code: ref.code,
      })),
    };
  } catch (error) {
    if (!error.response) {
      return {
        success: false,
        message: "Cannot connect to the server. Please try again later.",
        error: {
          code: "NETWORK_ERROR",
          message: error.message || "Network Error",
          solution: "Ensure the backend server is running and reachable.",
        },
      };
    }

    return {
      success: false,
      message:
        error.response?.data?.message ||
        "An error occurred while fetching referral codes.",
      error: error.response?.data?.error || {
        code: error.response?.status || 500,
        message: error.message || "Internal Server Error",
        solution: "Please try again later",
      },
    };
  }
};